// src/components/Auth/SignIn.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import googleIcon from "../../assets/images/others/google.svg";
import shapeIcon from "../../assets/images/contact/4.png";
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';

const SignIn = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    remember: false
  });
  const [loading, setLoading] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("error");
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.title = "Sign In | ProTradesInsight";
    
    // Check for message from localStorage (from verification redirect)
    const storedMessage = localStorage.getItem('loginMessage');
    if (storedMessage) {
      try {
        const { message, severity } = JSON.parse(storedMessage);
        showAlert(message, severity || 'success');
        localStorage.removeItem('loginMessage');
      } catch (error) {
        console.error('Error parsing login message:', error);
      }
    }
    
    // Check for message from sessionStorage (backup)
    const sessionMessage = sessionStorage.getItem('loginMessage');
    if (sessionMessage) {
      try {
        const { message, severity } = JSON.parse(sessionMessage);
        showAlert(message, severity || 'success');
        sessionStorage.removeItem('loginMessage');
      } catch (error) {
        console.error('Error parsing session login message:', error);
      }
    }
    
    // Check for message from location state
    if (location.state?.message) {
      showAlert(location.state.message, location.state.severity || 'success');
      // Clear the state
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: type === 'checkbox' ? checked : value
    }));
  };

  const togglePasswordVisibility = (fieldId) => {
    const input = document.getElementById(fieldId);
    if (input) {
      input.type = input.type === "password" ? "text" : "password";
    }
  };

  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenAlert(false);
  };

  const showAlert = (message, severity = "error") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setOpenAlert(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(
      formData.email,
      formData.password,
      formData.remember
    );

    if (result.success) {
      const from = location.state?.from?.pathname || '/dashboard';
      navigate(from, { replace: true });
    } else {
      // Check if verification is required
      if (result.requiresVerification) {
        localStorage.setItem('verificationEmail', formData.email);
        navigate('/verify-required', { 
          state: { 
            email: formData.email,
            message: 'Please verify your email before logging in.'
          } 
        });
        return;
      }
      showAlert(result.message || "Login failed. Please try again.");
    }

    setLoading(false);
  };

  return (
    <div className="signin-page">
      <style>
        {`
          /* Fix for autofill input styling */
          .account__form input:-webkit-autofill,
          .account__form input:-webkit-autofill:hover,
          .account__form input:-webkit-autofill:focus,
          .account__form input:-webkit-autofill:active {
            -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
            -webkit-text-fill-color: #000000 !important;
            background-color: #ffffff !important;
            background-clip: content-box !important;
            transition: background-color 5000s ease-in-out 0s;
          }

          .account__form input:autofill,
          .account__form input:autofill:hover,
          .account__form input:autofill:focus {
            box-shadow: 0 0 0 1000px #ffffff inset !important;
            -webkit-text-fill-color: #000000 !important;
            background-color: #ffffff !important;
          }

          .account__form .form-control {
            background-color: #ffffff !important;
            color: #000000 !important;
            border: 1px solid #ced4da !important;
            transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
          }

          .account__form .form-control:focus {
            background-color: #ffffff !important;
            color: #000000 !important;
            border-color: #000080 !important;
            box-shadow: 0 0 0 0.2rem rgba(0, 0, 128, 0.25) !important;
          }

          .account__form .form-control:-webkit-autofill {
            -webkit-box-shadow: 0 0 0 1000px #ffffff inset !important;
            -webkit-text-fill-color: #000000 !important;
          }

          .account__form .form-control:-webkit-autofill:focus {
            -webkit-box-shadow: 0 0 0 1000px #ffffff inset, 0 0 0 0.2rem rgba(0, 0, 128, 0.25) !important;
            -webkit-text-fill-color: #000000 !important;
          }

          .account__form .input-group .btn-outline-secondary {
            background-color: #ffffff !important;
            border-color: #ced4da !important;
            color: #6c757d !important;
          }

          .account__form .input-group .btn-outline-secondary:hover {
            background-color: #f8f9fa !important;
            border-color: #ced4da !important;
            color: #6c757d !important;
          }

          .account__form .input-group .form-control {
            border-right: none !important;
          }

          .account__form .input-group .btn-outline-secondary {
            border-left: none !important;
          }

          .account__form .input-group .form-control:focus {
            border-color: #000080 !important;
          }

          .account__form .input-group .form-control:focus + .btn-outline-secondary {
            border-color: #000080 !important;
          }

          .account__form .input-group .btn-outline-secondary i {
            font-size: 16px;
          }

          .account__check-remember input[type="checkbox"] {
            margin-right: 8px;
          }

          .account__check-remember label {
            margin-bottom: 0;
            cursor: pointer;
          }

          .account__form .form-label {
            font-weight: 500;
            color: #333333;
          }

          .account__check {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 16px;
          }
        `}
      </style>

      <Snackbar
        open={openAlert}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertSeverity}
          sx={{ width: '100%' }}
          variant="filled"
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      <div className="account padding-top padding-bottom sec-bg-color2">
        <div className="container">
          <div className="account__wrapper" dataAos="fade-up" dataAosDuration="800">
            <div className="row g-4">
              <div className="col-lg-12">
                <div className="account__content account__content--style1">
                  <div className="account__header">
                    <h2>Welcome back!</h2>
                    <p>
                      Hey there! Ready to log in? Just enter your email and password below.
                    </p>
                  </div>

                  <div className="account__social">
                    <button className="account__social-btn" type="button">
                      <span>
                        <img src={googleIcon} alt="Google icon" />
                      </span>
                      Continue with Google
                    </button>
                  </div>

                  <div className="account__divider account__divider--style1">
                    <span>or</span>
                  </div>

                  <form onSubmit={handleSubmit} className="account__form">
                    <div className="row g-4">
                      <div className="col-12">
                        <div>
                          <label htmlFor="email" className="form-label">Email</label>
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            placeholder="Enter your email"
                            required
                            value={formData.email}
                            onChange={handleChange}
                            autoComplete="email"
                          />
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-pass">
                          <label htmlFor="password" className="form-label">Password</label>
                          <div className="input-group">
                            <input
                              type="password"
                              className="form-control"
                              id="password"
                              placeholder="Password"
                              required
                              value={formData.password}
                              onChange={handleChange}
                              autoComplete="current-password"
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={() => togglePasswordVisibility('password')}
                              tabIndex="-1"
                            >
                              <i className={`fa ${formData.password ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="account__check">
                      <div className="account__check-remember">
                        <input 
                          type="checkbox" 
                          id="remember" 
                          className="form-check-input" 
                          checked={formData.remember}
                          onChange={handleChange}
                        />
                        <label htmlFor="remember" className="form-check-label">
                          Remember me
                        </label>
                      </div>
                      <div className="account__check-forgot">
                        <Link to="/forgot-password">Forgot Password?</Link>
                      </div>
                    </div>

                    <button 
                      type="submit" 
                      className="trk-btn trk-btn--border trk-btn--primary d-block mt-4"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Signing in...
                        </>
                      ) : 'Sign In'}
                    </button>
                  </form>

                  <div className="account__switch">
                    <p>
                      Don't have an account? <Link to="/signup">Sign up</Link>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="account__shape">
          <span className="account__shape-item account__shape-item--1">
            <img src={shapeIcon} alt="Shape icon" />
          </span>
        </div>
      </div>
    </div>
  );
};

export default SignIn;